'use client';

import { ColumnDef } from '@tanstack/react-table';
import { brandingConfig } from '@/config/branding';
import {
  CalendarIcon,
  CircleDashed,
  DollarSign,
  Ellipsis,
  Repeat,
  Shield,
  ShoppingCart,
  Truck,
  Zap,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatMoney } from "@/lib/money";
import { computeSubscriptionDisplayAmount } from '@/lib/utils/subscription-price';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { SubscriptionStatusBadge } from '@/components/subscriptions/subscription-status-badge';
import { Subscription } from '@/lib/api/subscriptions';
import { useStoreFilterOptions } from '../../../lib/api/stores';
import { DateDisplay } from '@/components/date-display';
import { DataTableCheckbox } from '@/components/data-table/data-table-checkbox';

interface GetSubscriptionsTableColumnsProps {
  onCancelSubscription: (subscriptionId: string, cancelAtPeriodEnd?: boolean) => void;
  onResumeSubscription: (subscriptionId: string) => void;
  onPauseSubscription: (subscriptionId: string) => void;
  isCanceling?: boolean;
  isResuming?: boolean;
  isPausing?: boolean;
  options: {
    'subscriptions.status': string[];
    'stores.id': { id: string; name: string }[];
    preferredProcessorId: { id: string; name: string }[];
  };
  t: (key: string) => string;
  orgRoutes: any;
}

// Utility functions

function formatInterval(interval: string, intervalCount: number, t: (key: string) => string) {
  const translatedKey = `filters.plans.${interval}`;
  const translated = t(translatedKey);
  const intervalName = translated === translatedKey ? interval.charAt(0).toUpperCase() + interval.slice(1) : translated;
  if (intervalCount === 1) {
    return intervalName;
  }
  return `${intervalCount} ${intervalName}`;
}

export function getSubscriptionsTableColumns({
  onCancelSubscription,
  onResumeSubscription,
  onPauseSubscription,
  isCanceling = false,
  isResuming = false,
  isPausing = false,
  options,
  t,
  orgRoutes,
}: GetSubscriptionsTableColumnsProps): ColumnDef<Subscription>[] {
  return [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label={t('table.selectAll')}
          className="translate-y-0.5"
        />
      ),
      cell: ({ row }) => (
        <DataTableCheckbox row={row} />
      ),
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
    // 2. Amount (moved to top)
    {
      id: 'amount',
      accessorKey: 'sortableAmount',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.amount')} />,
      cell: ({ row }) => {
        const subscription = row.original;
        const currency = subscription.currency || 'USD';
        const unitPrice = subscription.priceCurrencyOptions?.[currency]?.amount ?? 0;
        const quantity = subscription.quantity ?? 1;
        const pricingPolicies = subscription.priceSettings?.providerSpecific?.shopify?.pricingPolicies ?? null;
        const amount = computeSubscriptionDisplayAmount({ unitAmountMinor: unitPrice, quantity, pricingPolicies });
        return (
          <div className="flex flex-col">
            <div className="text-sm font-medium tabular-nums">{formatMoney(amount, currency)}</div>
            {currency !== 'USD' && (
              <div className="text-muted-foreground text-xs font-medium leading-none">{currency}</div>
            )}
          </div>
        );
      },
      meta: {
        label: t('table.columns.amount'),
        variant: 'money',
        icon: () => <DollarSign />,
        options: [
          { label: t('table.filters.moneyOptions.isEqualTo'), value: 'is-equal-to' },
          { label: t('table.filters.moneyOptions.isBetween'), value: 'is-between' },
          { label: t('table.filters.moneyOptions.isGreaterThan'), value: 'is-greater-than' },
          { label: t('table.filters.moneyOptions.isLessThan'), value: 'is-less-than' },
        ],
        formatValue: (minorUnits: number) => formatMoney(minorUnits, 'USD'),
        currency: 'USD',
      },
      enableColumnFilter: true,
      enableSorting: true,
      size: 120,
    },
    {
      id: 'quantity',
      accessorKey: 'quantity',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.quantity')} />,
      cell: ({ row }) => {
        const quantity = row.original.quantity ?? 1;
        return <div className="text-sm tabular-nums">{quantity}</div>;
      },
      enableColumnFilter: false,
      enableSorting: false,
      meta: { label: t('table.columns.quantity') },
      size: 80,
    },
    {
      id: 'customer',
      accessorKey: 'customerEmail',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.customer')} />,
      cell: ({ row }) => {
        const subscription = row.original;
        return (
          <div className="min-w-0">
            <div className="truncate font-medium">{subscription.customer || t('unknownCustomer')}</div>
            <div className="text-muted-foreground truncate text-xs">{subscription.customerEmail}</div>
          </div>
        );
      },
      enableColumnFilter: false,
      enableSorting: true,
      meta: { label: t('table.columns.customer') },
    },
    {
      id: 'store',
      accessorKey: 'storeName',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.store')} />,
      cell: ({ row }) => {
        const subscription = row.original;
        return <div className="truncate">{subscription.storeName}</div>;
      },
      meta: {
        label: t('table.columns.store'),
        variant: 'multiAsyncSelect',
        asyncFilterOptions: useStoreFilterOptions,
        icon: () => <ShoppingCart />,
      },
      enableColumnFilter: true,
      enableSorting: true,
    },
    {
      id: 'processor',
      accessorKey: 'processorName',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.processor')} />,
      cell: ({ row }) => {
        const subscription = row.original;
        return (
          <div className="flex items-center gap-2">
            <Zap className="text-muted-foreground h-4 w-4" />
            <span className="truncate">{subscription.processorName || t('unknownProcessor')}</span>
          </div>
        );
      },
      meta: {
        label: t('table.columns.processor'),
        variant: 'multiSelect',
        options: (options.preferredProcessorId || []).map((p) => ({
          label: p.name,
          value: p.id,
        })),
        icon: () => <Zap />,
      },
      enableColumnFilter: true,
      enableSorting: true,
    },
    {
      id: 'cardVault',
      accessorKey: 'useTagadaVault',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.cardVault')} />,
      cell: ({ row }) => {
        const subscription = row.original;
        const useTagadaVault = subscription.useTagadaVault;
        return (
          <div className="flex items-center gap-2">
            <Shield className="text-muted-foreground h-4 w-4" />
            <Badge variant={useTagadaVault ? 'default' : 'secondary'} className="text-xs">
              {useTagadaVault ? t('filters.vaultLabels.tagada') : t('filters.vaultLabels.external')}
            </Badge>
          </div>
        );
      },
      meta: {
        label: t('table.columns.cardVault'),
        variant: 'multiSelect',
        options: [
          { label: t('filters.vaultLabels.tagada'), value: 'true' },
          { label: t('filters.vaultLabels.external'), value: 'false' },
        ],
        icon: () => <Shield />,
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.status')} />,
      cell: ({ row }) => {
        const subscription = row.original;
        const cancelAtPeriodEnd = subscription.cancelAtPeriodEnd;
        const status = cancelAtPeriodEnd ? 'canceled' : (subscription.status ?? 'unknown');
        return <SubscriptionStatusBadge status={status} />;
      },
      meta: {
        label: t('table.columns.status'),
        variant: 'multiSelect',
        options: [
          { label: t('filters.statuses.active'), value: 'active' },
          { label: t('filters.statuses.trialing'), value: 'trialing' },
          { label: t('filters.statuses.past_due'), value: 'past_due' },
          { label: t('filters.statuses.canceled'), value: 'canceled' },
          { label: t('filters.statuses.paused'), value: 'paused' },
        ],
        icon: () => <CircleDashed />,
      },
      enableColumnFilter: true,
      enableSorting: true,
    },
    {
      id: 'plan',
      accessorKey: 'priceInterval',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.plan')} />,
      cell: ({ row }) => {
        const subscription = row.original;
        const interval = subscription.priceInterval || 'month';
        const intervalCount = subscription.priceIntervalCount || 1;
        return (
          <div className="flex items-center gap-2">
            <Repeat className="text-muted-foreground h-4 w-4" />
            <Badge variant="secondary">
              {formatInterval(interval, intervalCount, t)}
            </Badge>
          </div>
        );
      },
      meta: {
        label: t('table.columns.plan'),
        variant: 'multiSelect',
        options: [
          { label: t('filters.plans.day'), value: 'day' },
          { label: t('filters.plans.week'), value: 'week' },
          { label: t('filters.plans.month'), value: 'month' },
          { label: t('filters.plans.year'), value: 'year' },
        ],
        icon: () => <Repeat />,
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    {
      id: 'paymentType',
      accessorKey: 'paymentType',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('table.columns.paymentType')} />
      ),
      cell: ({ row }) => {
        const subscription = row.original;
        const getPaymentTypeLabel = (paymentType: string | null): string => {
          switch (paymentType) {
            case 'card':
              return t('filters.paymentTypes.card');
            case 'apple_pay':
              return t('filters.paymentTypes.apple_pay');
            default:
              return t('filters.paymentTypes.external');
          }
        };
        return (
          <Badge variant="gray" className="min-w-[72px] justify-center">
            {getPaymentTypeLabel(subscription.paymentType)}
          </Badge>
        );
      },
      meta: {
        label: t('table.columns.paymentType'),
        variant: 'multiSelect',
        options: [
          { label: t('filters.paymentTypes.card'), value: 'card' },
          { label: t('filters.paymentTypes.apple_pay'), value: 'apple_pay' },
        ],
        icon: () => <Repeat />,
      },
      enableColumnFilter: true,
      enableSorting: false,
    },
    {
      id: 'shippingRate',
      accessorKey: 'shippingRateId',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('table.columns.shippingRate')} />
      ),
      cell: ({ row }) => {
        const subscription = row.original;
        const { shippingRateId, storeId } = subscription;
        if (!shippingRateId) {
          return <div className="text-muted-foreground text-sm">-</div>;
        }
        if (!storeId) {
          return (
            <Badge variant="secondary" className="text-xs">
              <Truck className="mr-1 h-3 w-3" />
              {`${shippingRateId.substring(0, 10)}...`}
            </Badge>
          );
        }
        return (
          <Link to={`${orgRoutes.STORE(storeId)}/general/shippings/${shippingRateId}`}>
            <Badge variant="secondary" className="cursor-pointer text-xs hover:bg-accent">
              <Truck className="mr-1 h-3 w-3" />
              {`${shippingRateId.substring(0, 10)}...`}
            </Badge>
          </Link>
        );
      },
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      id: 'nextBillingDate',
      accessorKey: 'nextBillingDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('table.columns.nextBilling')} />
      ),
      cell: ({ row }) => (
        <DateDisplay value={row.original.nextBillingDate} mode="date" className="text-sm" />
      ),
      meta: {
        label: t('table.columns.nextBilling'),
        variant: 'dateRange',
        icon: () => <CalendarIcon />,
      },
      enableColumnFilter: true,
      enableSorting: true,
    },
    {
      id: 'createdAt',
      accessorKey: 'createdAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.created')} />,
      cell: ({ row }) => (
        <DateDisplay value={row.original.createdAt} mode="date" className="text-sm" />
      ),
      meta: {
        label: t('table.columns.created'),
        variant: 'dateRange',
        icon: () => <CalendarIcon />,
      },
      enableColumnFilter: true,
      enableSorting: true,
    },
    {
      id: 'updatedAt',
      accessorKey: 'updatedAt',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('table.columns.updated')} />,
      cell: ({ row }) => (
        <DateDisplay value={row.original.updatedAt} mode="date" className="text-sm" />
      ),
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        label: t('table.columns.updated'),
        variant: 'dateRange',
        icon: () => <CalendarIcon />,
      },
    },
    // Subscription ID (moved to end, copyable)
    {
      id: 'id',
      accessorKey: 'id',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('table.columns.subscriptionId')} />
      ),
      cell: ({ row }) => {
        const subscription = row.original;
        const displayValue = `${subscription.id.substring(0, 8)}...`;
        return (
          <CopyableResourceBadge
            name={t('table.columns.subscriptionId')}
            value={displayValue}
            copyValue={subscription.id}
          />
        );
      },
      enableSorting: true,
      enableHiding: true,
      size: 140,
    },
    {
      id: 'group',
      accessorKey: 'subscriptionGroupId',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('table.columns.group')} />
      ),
      cell: ({ row }) => {
        const subscriptionGroupId = row.original.subscriptionGroupId;
        if (!subscriptionGroupId) {
          return null;
        }
        const displayValue = `${subscriptionGroupId.substring(0, 8)}...`;
        return (
          <CopyableResourceBadge
            name={t('table.columns.group')}
            value={displayValue}
            copyValue={subscriptionGroupId}
          />
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      enableHiding: true,
      size: 140,
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const subscription = row.original;
        const status = subscription.status || 'unknown';
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                aria-label={t('table.openMenu')}
                variant="ghost"
                className="data-[state=open]:bg-muted flex h-8 w-8 p-0"
              >
                <Ellipsis className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[160px]">
              <DropdownMenuItem asChild>
                <Link to={orgRoutes.SUBSCRIPTION_DETAILS(subscription.id)}>
                  {t('table.actions.viewDetails')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {status === 'active' && (
                <DropdownMenuItem onClick={() => onPauseSubscription(subscription.id)} disabled={isPausing}>
                  {isPausing ? t('bulkActions.pause.button') + '...' : t('table.actions.pause')}
                </DropdownMenuItem>
              )}
              {status === 'paused' && (
                <DropdownMenuItem onClick={() => onResumeSubscription(subscription.id)} disabled={isResuming}>
                  {isResuming ? t('bulkActions.resume.button') + '...' : t('table.actions.resume')}
                </DropdownMenuItem>
              )}
              {(status === 'active' || status === 'trialing') && (
                <DropdownMenuItem
                  onClick={() => onCancelSubscription(subscription.id, true)}
                  disabled={isCanceling}
                  className="text-destructive focus:text-destructive"
                >
                  {isCanceling ? t('bulkActions.cancel.button') + '...' : t('table.actions.cancel')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
      enableSorting: false,
      enableHiding: false,
      size: 40,
    },
  ];
}
